import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { StoreServices } from "../../../core/services/StoreServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { StoreFields, StoreListParams } from "./StoreDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./Stores.css";
import StoreFilters from "./StoreFilters";
import { isEmptyVal, isNotEmpty, displayField } from "../../../utils/helpers";
import AddressCell from "../../components/Elements/AddressCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import StoreSingleAction from "./StoreSingleAction";
import StoreBulkAction from "./StoreBulkAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
  roleTypeArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import ContactPersonCell from "../../components/Elements/ContactPersonCell";
import ManagerCell from "./ManagerCell";
import PhoneCell from "../../components/Elements/PhoneCell";

function Stores() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<StoreFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const company = useSelector((state: any) => state.company);
  const [sort_order, setSortOrder] = useState<any>();
  const [selectedStore, setSelectedStoreData] = useState<any>({});
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const initSearchFormData = {
    name: "",
    code: "",
    mobile: "",
    region: "",
    group: "",
    unassigned_stores: "",
    type: "",
    building: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    contact_person: "",
    contact_person_email: "",
    contact_person_phone: "",
    prime_comp_admin: "",
    prime_comp_admin_email: "",
    prime_comp_admin_phone: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] = useState<StoreListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setPage(1);
    getStores(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }, [company]);

  async function getStores(params: StoreListParams) {
    try {
      setLoading(true);
      const res = await StoreServices.getStores(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function init() {
    setShowCreate(UtilityServices.hasPermission("create-store"));
  }

  useEffect(() => {
    init();
  }, []);

  async function deleteStore(store: any) {
    try {
      let res = await StoreServices.deleteStore(store?.current?.id);
      setPage(1);
      getStores(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function publishStore(store: any) {
    try {
      let status =
        store?.current?.status == statusArr.inactive
          ? statusArr.active
          : statusArr.inactive;

      let res = await StoreServices.publishStore(store?.current?.id, {
        status,
      });
      setPage(1);
      getStores(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function refreshListData() {
    try {
      getStores(getSearchParams());
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkPublishAction(status: string) {
    try {
      let res = await StoreServices.publishBulkStore(checkedKeys, { status });
      setPage(1);
      getStores(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkDeleteAction() {
    try {
      let res = await StoreServices.deleteBulkStore(checkedKeys);
      setPage(1);
      getStores(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkAssignStoreGroupAction(groupId: string) {
    try {
      let res = await StoreServices.assignBulkStoreGroup(checkedKeys, {
        store_group_id: groupId,
      });
      setPage(1);
      getStores(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function exportStoreAction() {
    try {
      await StoreServices.exportStores(getSearchParams());
    } catch (error) {
      console.log(error);
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: StoreListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    if (company?.id) {
      params.company = company.id;
    }

    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getStores(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getStores(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getStores(getSearchParams(formData));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getStores(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterStores = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getStores(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getStores(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getStores(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const publishPermission = UtilityServices.hasPermission("publish-store");
  const updatePermission = UtilityServices.hasPermission("update-store");
  const deletePermission = UtilityServices.hasPermission("delete-store");
  const createUserPermission = UtilityServices.hasPermission("create-user");
  const importProductPermission = UtilityServices.hasPermission(
    "import-product-for-store"
  );
  const viewStoreProductPermission =
    UtilityServices.hasPermission("view-store-product");
  const permissionObj: Permission = {
    publish: publishPermission,
    update: updatePermission,
    delete: deletePermission,
    create_user: createUserPermission,
    import_product: importProductPermission,
    view_store_product: viewStoreProductPermission,
  };

  return (
    <div className="stores customers ">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>{t("STORES")}</h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button
                  appearance="primary"
                  onClick={() => navigate(ContentRoutes.CREATE_STORE)}
                >
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content>
          <StoreFilters onSubmit={submitSearch} onClear={clearSearch} />
          <div className="row">
            <StoreBulkAction
              checkedKeys={checkedKeys}
              setCheckedKeys={setCheckedKeys}
              onBulkPublish={bulkPublishAction}
              onBulkDelete={bulkDeleteAction}
              onExportStore={exportStoreAction}
              onBulkAssignStoreGroup={bulkAssignStoreGroupAction}
            />
            <div className="col-md-4">
              <InputGroup>
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("STORE").toLowerCase() +
                      " " +
                      t("NAME").toLowerCase() +
                      ", " +
                      t("STORE").toLowerCase() +
                      " " +
                      t("CODE").toLowerCase() +
                      ", " +
                      t("OR").toLowerCase() +
                      " " +
                      t("MOBILE").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterStores(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterStores(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>

          <div className={total > 0 ? "usr-tbl-border" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>

              <Column width={200} sortable resizable fixed>
                <CustomHeaderCell>
                  {t("STORE")} {t("NAME")}
                </CustomHeaderCell>
                <CustomCell dataKey="store_name">
                  {(rowData: any) => (
                    <span
                      title={
                        (isNotEmpty(`${rowData.store_code}`) &&
                          `${rowData.store_code}`) ||
                        `${rowData.store_name}`
                      }
                    >
                      {displayField(rowData.store_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>

              <Column width={160} resizable fixed>
                <CustomHeaderCell>{t("MANAGER")}</CustomHeaderCell>
                <ManagerCell rowData={(rowData: any) => rowData} />
              </Column>

              <Column width={160}>
                <CustomHeaderCell>{t("CONTACT_PERSON")}</CustomHeaderCell>
                <ContactPersonCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={180} resizable>
                <CustomHeaderCell>{t("ADDRESS")}</CustomHeaderCell>
                <AddressCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={120}>
                <CustomHeaderCell>{t("PHONE")}</CustomHeaderCell>
                <PhoneCell
                  positionFromRight="30%"
                  rowData={(rowData: any) => rowData}
                />
              </Column>
              <Column width={130} resizable>
                <CustomHeaderCell>{t("GROUP")}</CustomHeaderCell>
                <CustomCell dataKey="group_name">
                  {(rowData: any) => (
                    <span
                      title={
                        isNotEmpty(`${rowData.region_name}`) &&
                        t("REGION") + ": " + `${rowData.region_name}`
                      }
                    >
                      {displayField(rowData.group_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={120} resizable>
                <CustomHeaderCell>{t("REGION")}</CustomHeaderCell>
                <CustomCell dataKey="region_name" />
              </Column>
              <Column width={140} resizable>
                <CustomHeaderCell>{t("COMPANY")}</CustomHeaderCell>
                <CustomCell dataKey="comp_name" />
              </Column>
              <Column width={150} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => (
                    <StoreSingleAction
                      storeData={rowData}
                      onDeleteStore={deleteStore}
                      onPublishStore={publishStore}
                      permission={permissionObj}
                      refreshList={refreshListData}
                    />
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <ToastContainer />
      </Container>
    </div>
  );
}

export default Stores;
